<script setup lang="ts">
    import { reactive, onMounted, inject, computed } from 'vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import Loading from '@shared/components/Loading.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { debounce } from '@shared/utils';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { CurrentAccountReference } from '@shared/Root/Context.access';
    import { loadTags } from '@shared/components/Tags/Tags.access';
    import { type Tag } from '@shared/components/Tags/TagsTypes';
    import { getCategories } from '@views/Issues/Issues.access';
    import { type Category } from '@views/Issues/interfaces';
    import { get, update } from './AccountInfo.access';
    import { getValidatedEmail, sendReplyAddressValidation } from './AccountMailSettings.access';
    import AccountDataSources from './AccountDataSources.vue';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const hasDataSourcesFeature = computed(() => featureAccess.hasFeature('Email.AdvancedBlocks'));

    const trackingOptions = [
        { label: $Resources.StandardTrackingLabel, value: '0' },
        { label: $Resources.UTMTrackingLabel, value: '1' },
        { label: $Resources.UngappedIdTrackingLabel, value: '2' },
        { label: $Resources.UTMAndUngappedIdTrackingLabel, value: '3' },
        { label: $Resources.AnonymTrackingLabel, value: '4' },
        { label: $Resources.UTMAndAnonymousTrackingLabel, value: '6' },
        { label: $Resources.NoTrackingLabel, value: '5' },
    ];

    const state = reactive<{
        isLoading: boolean;
        account: CurrentAccountReference | null;
        defaultFromName: string;
        defaultTrackingOptions: string;
        replyAddress: {
            value: string;
            isValidated: boolean;
            validationSent: boolean;
            isBusy: boolean;
        };
        archive: {
            loaded: boolean;
            isLoading: boolean;
            categories: Category[];
            tags: Tag[];
        };
    }>({
        isLoading: true,
        account: null,
        defaultFromName: '',
        defaultTrackingOptions: '0',
        replyAddress: { value: '', isValidated: false, validationSent: false, isBusy: false },
        archive: { loaded: false, isLoading: false, categories: [], tags: [] },
    });

    onMounted(async () => {
        state.account = await get();

        state.defaultFromName = state.account.DefaultFromName;
        state.defaultTrackingOptions = String(state.account.DefaultTrackingOptions);

        loadNewsletterArchive();

        state.replyAddress.value = state.account.DefaultReplyToAddress || '';
        state.replyAddress.isValidated = state.account.DefaultReplyToAddressIsValidated;

        state.isLoading = false;
    });

    function propertyLabel(key: string, fallback: string): string {
        const resources = $Resources as unknown as Record<string, string>;
        return resources['PropertyAccount' + key + 'Label'] || fallback;
    }

    async function saveProperty(key: string, value: any): Promise<void> {
        if (!state.account) return;

        try {
            await update(key, value);
            (state.account as any)[key] = value;
            bsAlert({ type: AlertTypes.Success, title: $Resources.PropertyUpdatedLabel, content: propertyLabel(key, $Resources.AccountDefaultSenderHeading) });
        } catch {
            bsAlert({
                type: AlertTypes.Danger,
                title: $Resources.PropertyNotUpdatedLabel,
                content: propertyLabel(key, $Resources.AccountDefaultSenderHeading),
            });
        }
    }

    function onDefaultFromNameInput(newVal: string): void {
        state.defaultFromName = newVal;

        debounce(
            () => {
                saveProperty('DefaultFromName', newVal);
            },
            { delay: 1500 },
            2
        );
    }

    function onDefaultTrackingOptionsChange(newVal: string): void {
        state.defaultTrackingOptions = newVal;
        saveProperty('DefaultTrackingOptions', parseInt(newVal, 10));
    }

    function onReplyAddressInput(newVal: string): void {
        state.replyAddress.value = newVal;
        state.replyAddress.validationSent = false;

        debounce(
            () => {
                saveProperty('DefaultReplyToAddress', newVal);
                validateReplyAddress(newVal);
            },
            { delay: 500 },
            1
        );
    }

    async function validateReplyAddress(email: string, onClick?: boolean): Promise<void> {
        if (!email) return;

        state.replyAddress.isBusy = true;
        state.replyAddress.isValidated = false;

        try {
            const result = await getValidatedEmail(email);
            state.replyAddress.isValidated = result.IsValidated;

            if (onClick && result.IsValidated) {
                bsAlert();
            } else if (onClick) {
                bsAlert({ type: AlertTypes.Danger });
            }
        } finally {
            state.replyAddress.isBusy = false;
        }
    }

    async function sendReplyAddressValidationEmail(): Promise<void> {
        try {
            await sendReplyAddressValidation(state.replyAddress.value);
            state.replyAddress.validationSent = true;
        } catch {
            bsAlert({ type: AlertTypes.Danger, content: $Resources.ErrorValidatedSenderEmailNotSent });
        }
    }

    async function loadNewsletterArchive(): Promise<void> {
        if (state.archive.loaded) return;

        state.archive.isLoading = true;

        const [categories, tags] = await Promise.all([getCategories(), loadTags()]);

        state.archive.categories = categories.filter((category) => category.IsPublic);
        state.archive.tags = tags;
        state.archive.loaded = true;
        state.archive.isLoading = false;
    }
</script>

<template>
    <Loading :show="state.isLoading" loading-style="progress-bar" fixed></Loading>

    <div v-if="state.account">
        <h1 class="h2 m-lg-b">
            {{ $Resources.AccountTabMailSettings }}
            <small>({{ state.account.Name }})</small>
        </h1>

        <p
            class="m-lg-b"
            v-html="$Resources.AccountPageTabsAccountTextWithLink.replace('{{PreviewPreferenceCenterUrl}}', state.account?.PreviewPreferenceCenterUrl)"></p>

        <Tabs>
            <Tab :title="$Resources.AccountDefaultSenderTab">
                <div class="max-700">
                    <h2 class="h3">{{ $Resources.AccountDefaultSenderHeading }}</h2>

                    <FormInput
                        class="form-group"
                        :modelValue="state.defaultFromName"
                        :label="$Resources.AccountDefaultFromNameLabel"
                        :placeholder="$Resources.AccountDefaultFromNamePlaceholder"
                        :required="true"
                        :maxlength="100"
                        @update:modelValue="onDefaultFromNameInput"></FormInput>

                    <div class="form-group">
                        <FormInput
                            :class="!state.replyAddress.isValidated && 'form-group-invalid'"
                            type="email"
                            :label="$Resources.ReplyToAddressLabel"
                            :placeholder="$Resources.AccountDefaultReplyToAddressPlaceholder"
                            :modelValue="state.replyAddress.value"
                            @update:modelValue="onReplyAddressInput"></FormInput>

                        <div v-show="state.replyAddress.value">
                            <BsAlert
                                v-if="!state.replyAddress.isValidated && !state.replyAddress.validationSent && !state.replyAddress.isBusy"
                                :type="AlertTypes.Danger"
                                :dismissible="false"
                                :title="$Resources.AlertSendDialogNoValidatedReplySenderEmailHeading"
                                :content="$Resources.AlertSendDialogNoValidatedReplySenderEmailBody"
                                :buttonText="$Resources.ValidateReplySenderAddressButton"
                                @buttonClick="sendReplyAddressValidationEmail"></BsAlert>

                            <BsAlert
                                v-if="!state.replyAddress.isValidated && state.replyAddress.validationSent && !state.replyAddress.isBusy"
                                :type="AlertTypes.Info"
                                :dismissible="false"
                                :content="$Resources.AlertValidatedSenderEmailSent"
                                :buttonText="$Resources.RefreshValidationStatusBtnText"
                                @buttonClick="validateReplyAddress(state.replyAddress.value, true)"></BsAlert>
                        </div>

                        <Loading :show="state.replyAddress.isBusy" loadingStyle="progress-bar" fixed></Loading>
                    </div>

                    <FormRadioGroup
                        class="form-group"
                        :modelValue="state.defaultTrackingOptions"
                        :legend="$Resources.UseGoogleAnalyticsHeading"
                        :options="trackingOptions"
                        @update:modelValue="onDefaultTrackingOptionsChange"></FormRadioGroup>
                </div>
            </Tab>

            <Tab :title="$Resources.NewsletterArchiveTabTitle">
                <div class="max-700">
                    <h2 class="h3">{{ $Resources.NewsletterArchiveSubscriptionsTitle }}</h2>
                    <p>{{ $Resources.NewsletterArchiveSubscriptionsInfo }}</p>

                    <BsAlert
                        v-if="state.archive.loaded && state.archive.categories.length === 0"
                        :type="AlertTypes.Info"
                        :dismissible="false"
                        :title="$Resources.NewsletterArchiveFoundNothingAlertHeading"
                        :content="$Resources.NewsletterArchiveFoundNothingAlertBody"></BsAlert>

                    <ul class="list-unstyled m-lg-b">
                        <li v-for="category in state.archive.categories" :key="category.CategoryId">
                            <div class="h4 m-xs-b">
                                <a
                                    :href="combineUrlSegments(state.account.PublicEventUrl.PublicHost, 'Issues', 'Archive', 'Categories', category.CategoryId)"
                                    target="_blank"
                                    rel="noopener">
                                    {{ category.Name }}
                                    <span class="glyphicon glyphicon-new-window c-light text-md m-xxs-l" aria-hidden="true"></span>
                                </a>
                            </div>
                            <p>{{ category.Description }}</p>
                        </li>
                    </ul>

                    <h2 class="h3 m-lg-b">{{ $Resources.NewsletterArchiveTagsTitle }}</h2>

                    <BsAlert
                        v-if="state.archive.loaded && state.archive.tags.length === 0"
                        :type="AlertTypes.Info"
                        :dismissible="false"
                        :title="$Resources.NewsletterArchiveFoundNothingAlertHeading"
                        :content="$Resources.NewsletterArchiveFoundNothingAlertBody"></BsAlert>

                    <ul class="list-unstyled tag-list m-lg-b">
                        <li v-for="tag in state.archive.tags" :key="tag.TagId">
                            <a
                                :href="combineUrlSegments(state.account.PublicEventUrl.PublicHost, 'Issues', 'Archive', 'Tags', tag.TagId)"
                                target="_blank"
                                rel="noopener"
                                class="tag tag-archive d-flex text-md c-white">
                                {{ tag.Title }}
                                <span class="glyphicon glyphicon-new-window m-sm-l o-60" aria-hidden="true"></span>
                            </a>
                        </li>
                    </ul>

                    <Loading :show="state.archive.isLoading" loadingStyle="progress-bar" fixed></Loading>
                </div>
            </Tab>

            <Tab v-if="hasDataSourcesFeature" :title="$Resources.DataSourcesHeading">
                <div class="max-900">
                    <AccountDataSources :account="state.account"></AccountDataSources>
                </div>
            </Tab>
        </Tabs>
    </div>
</template>

<style>
    .tag-archive {
        padding: var(--ug-spacing-sm) var(--ug-spacing-md);
    }
</style>

